<template>
  <StudioDialog
    :title="`Set a new password for ${currentUser.email}`"
    :showCloseButton="false"
    :primaryActionTitle="isSubmitting ? 'Submitting...' : 'Submit'"
    :primaryActionDisabled="!newPassword || !confirmPassword || newPassword !== confirmPassword || isSubmitting"
    @primaryAction="newPasswordSubmit"
  >
    <div class="tw-p-4">
      <StudioInput
        id="new-password-input"
        ref="newPasswordRef"
        v-model="newPassword"
        type="password"
        label="New Password"
        placeholder="Please enter a new password"
        class="tw-mb-4"
        required
        :minlength="8"
        :maxlength="40"
      />
      <StudioInput
        id="confirm-new-password-input"
        ref="confirmPasswordeRef"
        v-model="confirmPassword"
        type="password"
        label="Confirm Password"
        placeholder="Please re-enter the new password"
        required
        :minlength="8"
        :maxlength="40"
      />
    </div>
  </StudioDialog>
</template>

<script lang="ts">
import { setCognitoUserPasswordUser } from '@goldcast/api/backend';
import logger from '@goldcast/logger';
import { Auth } from 'aws-amplify';
import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import StudioDialog from '@/studioComponents/StudioDialog.vue';
import StudioInput from '@/studioComponents/StudioInput.vue';
import { updatePasswordLastSet } from '@/utils/cognito';
import { validatePassword } from '@/utils/email';

export default defineComponent({
  name: 'SetNewPasswordDialog',
  components: {
    StudioInput,
    StudioDialog
  },
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
      isSubmitting: false
    };
  },
  computed: {
    ...mapGetters('user', { currentUser: 'getCurrentUser' })
  },
  methods: {
    ...mapMutations('user', {
      toggleShowSetNewPasswordDialog: 'toggleShowSetNewPasswordDialog',
      setForceChangePassword: 'setForceChangePassword'
    }),
    ...mapActions('studioToast', { triggerToast: 'triggerToast' }),
    async newPasswordSubmit() {
      const newPasswordInput = this.$refs.newPasswordRef as HTMLInputElement;

      if (!validatePassword(this.newPassword)) {
        newPasswordInput.setCustomValidity(
          `Password must be between 8 and 32 characters long and include at least one uppercase letter, one lowercase letter, one digit (0-9), and one special character ('@', #, $, etc.).`
        );
        return false;
      }

      try {
        this.isSubmitting = true;
        await setCognitoUserPasswordUser({ id: this.currentUser.id, body: { password: this.newPassword } });
        const cognitoUser = await Auth.currentAuthenticatedUser().catch(() => null);
        if (cognitoUser) {
          await updatePasswordLastSet(cognitoUser);
        }
        this.setForceChangePassword(false);
        this.triggerToast({ msg: 'Password successfully updated.', mode: 'success' });
      } catch (error: any) {
        logger.warn('[SetNewPasswordDialog] Failed to set new password', error);
        this.triggerToast({
          msg: 'Failed to set new password. If the problem persists, please contact support.',
          mode: 'error'
        });
      } finally {
        this.isSubmitting = false;
        this.toggleShowSetNewPasswordDialog(false);
      }
    }
  }
});
</script>
